import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fileToDataUrl } from "@/lib/format";

const EMPTY = { code: "", name: "", description: "", category: "", type: "producto", cost_price: 0, sale_price: 0, stock: 0, min_stock: 0, image: "" };

const Field = ({ label, children, className = "" }) => (
  <div className={`space-y-1.5 ${className}`}>
    <Label className="label-cap">{label}</Label>
    {children}
  </div>
);

export const ProductDialog = ({ open, onOpenChange, product, categories, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF(product ? { ...EMPTY, ...product } : EMPTY);
  }, [open, product]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));

  const onImage = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const img = await fileToDataUrl(file, 500);
      setF((x) => ({ ...x, image: img }));
    }
  };

  const save = async () => {
    if (!f.code.trim() || !f.name.trim()) return toast.error("Código y nombre son obligatorios");
    setSaving(true);
    const body = { ...f, category: f.category || "General" };
    ["cost_price", "sale_price", "stock", "min_stock"].forEach((k) => (body[k] = Number(body[k]) || 0));
    try {
      if (product) await api.put(`/products/${product.id}`, body);
      else await api.post("/products", body);
      toast.success(product ? "Producto actualizado" : "Producto creado");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#120E2E] border-[#271E56] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="product-dialog">
        <DialogHeader>
          <DialogTitle className="text-white font-display">{product ? "Editar artículo" : "Nuevo artículo"}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Código"><Input value={f.code} onChange={set("code")} data-testid="product-code-input" /></Field>
          <Field label="Tipo">
            <Select value={f.type} onValueChange={set("type")}>
              <SelectTrigger data-testid="product-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="producto">Producto (con stock)</SelectItem>
                <SelectItem value="servicio">Servicio (sin stock)</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Nombre" className="sm:col-span-2"><Input value={f.name} onChange={set("name")} data-testid="product-name-input" /></Field>
          <Field label="Descripción" className="sm:col-span-2"><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="product-desc-input" /></Field>
          <Field label="Categoría">
            <Input list="cat-list" value={f.category} onChange={set("category")} placeholder="Ej: Redes" data-testid="product-category-input" />
            <datalist id="cat-list">{categories.map((c) => <option key={c} value={c} />)}</datalist>
          </Field>
          <Field label="Precio de costo (₡)"><Input type="number" min="0" value={f.cost_price} onChange={set("cost_price")} data-testid="product-cost-input" /></Field>
          <Field label="Precio de venta (₡)"><Input type="number" min="0" value={f.sale_price} onChange={set("sale_price")} data-testid="product-price-input" /></Field>
          {f.type === "producto" && (
            <>
              <Field label={product ? "Stock actual (use Movimientos)" : "Stock inicial"}>
                <Input type="number" min="0" value={f.stock} disabled={!!product} onChange={set("stock")} data-testid="product-stock-input" />
              </Field>
              <Field label="Stock mínimo"><Input type="number" min="0" value={f.min_stock} onChange={set("min_stock")} data-testid="product-minstock-input" /></Field>
            </>
          )}
          <Field label="Imagen" className="sm:col-span-2">
            <div className="flex items-center gap-4">
              {f.image ? (
                <div className="relative">
                  <img src={f.image} alt="" className="w-20 h-20 rounded-xl object-cover border border-[#271E56]" />
                  <button className="absolute -top-2 -right-2 bg-rose-500 rounded-full p-1" onClick={() => setF((x) => ({ ...x, image: "" }))} data-testid="product-image-remove">
                    <X className="w-3 h-3 text-white" />
                  </button>
                </div>
              ) : (
                <div className="w-20 h-20 rounded-xl border border-dashed border-[#271E56] flex items-center justify-center text-slate-500"><ImagePlus /></div>
              )}
              <label className="cursor-pointer text-sm text-[#00F0FF] hover:underline">
                Subir imagen
                <input type="file" accept="image/*" className="hidden" onChange={onImage} data-testid="product-image-input" />
              </label>
            </div>
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="product-cancel-btn">Cancelar</Button>
          <Button className="btn-neon" onClick={save} disabled={saving} data-testid="product-save-btn">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
